
 /*
  *****************************************************************************
  * WARNING: This file is automatically generated.
  * Do not edit this file manually.
  *****************************************************************************
  * 
  * The source file is located at:
  *   /codebuild/output/src994476363/src/vl-app/pages/login.tsx
  */

import { createFileRoute } from '@tanstack/react-router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { Alert, Button, Form, AlertProps } from 'antd';
import * as actions from '@actions';
import * as actionTypes from '@actionTypes';
import { useTranslation } from 'react-i18next';
import ReactGA from 'react-ga4';
import { useQueryState, parseAsString } from '@lib/queryState';
import { Auth, CognitoUser } from 'aws-amplify';
import { useInitialisedStore } from '@hooks/useInitialisedStore';
import { usePolicies } from '@hooks/usePolicies';
import { useRuntimeConfig } from '@vl-core/hooks/useConfig';
import { infoModal } from 'vl-common/src/components/Modals';
import LoginLayout from '@components/Layouts/Login';
import useSetTourAttributes from '@hooks/useSetTourAttributes';
import useBrowserCheckCallback from '@hooks/useBrowserCheckCallback';
import useAuth from '@vl-core/useAuth';
import LoginView from '@components/Login/views/LoginView';
import SSOView from '@components/Login/views/SSOView';
import MFAView from '@components/Login/views/MFAView';
import NewPasswordView from '@components/Login/views/NewPasswordView';
import saveAuthAndRedirect, { getIsLocalhostOrNgrok } from 'vl-common/src/lib/saveAuthAndRedirect';
import { Maintenance } from 'vl-common/src/components/Maintenance';
import { User } from 'vl-common/src/schemas/shared/user';
import { ErrorModal } from 'vl-common/src/components/Modals/CustomModals/Error';
import { css } from '@emotion/react';
import useAutoSSO from '@hooks/useAutoSSO';
import { useDeepLink } from '@hooks/useDeepLink';
import { StyledAntModal } from 'vl-common/src/components/StyledAntModal/StyledAntModal';
import { BVT_LOGIN_PATH } from './hbsbvt';
import { tryGetUser } from '../store/actions/auth';
import { requestSuccess } from '../store/actions/action';
import { deriveAppRoute } from 'vl-common/src/init/init';
import { signOut } from 'ALIASED-aws-amplify/auth';

const MFA_ATTEMPTS_EXCEEDED_CODE = 'AADB2C90151';

// Function to reverse a character shifted string
const unshiftString = (str: string, shiftBy: number) =>
  str
    .split('')
    .map((char) => String.fromCharCode(char.charCodeAt(0) - shiftBy))
    .join('');

function maybePersistCredentials() {
  try {
    const o = JSON.parse(decodeURI(window.location.hash.slice(1)));

    // since this originated from sessionStorage (whose values are all strings) this generic type is reasonable
    Object.entries<string>(o)
      .map(([k, v]) => [unshiftString(k, 3), v])
      .filter(([k]) =>
        ['accessToken', 'clockDrift', 'idToken', 'refreshToken', 'userData', 'LastAuthUser'].find((p) => k.endsWith(p))
      )
      .forEach(([k, v]) => window.sessionStorage.setItem(k, v));

    window.location.hash = '';
    window.location.reload();
  } catch {
    // don't care
  }
}

export type NotificationState = Pick<AlertProps, 'type' | 'message'>;

const Login = () => {
  const { APP_CLIENT_ID, COGDR, GOOGLE_ANALYTICS_TAG, IDENTITY_PROVIDERS, LOGIN_VERSION, SYSTEM_MODE } =
    useRuntimeConfig();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { pathname, replace } = useRouter();

  const { pushToApp } = useDeepLink();

  // normally I'd take the url parameters from the router, but it seems that on its first
  // call the parameters are null
  const urlParams = Object.fromEntries(new URLSearchParams(window.location.search).entries());
  const isOptOut = urlParams.error_description?.includes('ConsentNotGiven');

  const VALID_PAGE_VIEWS = ['sso', 'login', 'code', 'newPassword'] as const;
  type PageView = (typeof VALID_PAGE_VIEWS)[number];
  function isValidPageView(pageView: string): pageView is PageView {
    return VALID_PAGE_VIEWS.includes(pageView as PageView);
  }

  const { pageView: initialPageView = 'sso', userdata, error_description, ...additionalMetaData } = urlParams;

  const router = useRouter();
  const [pageView, setPageView] = useState<PageView | ''>(isValidPageView(initialPageView) ? initialPageView : '');
  const [buttonLoading, setButtonLoading] = useState(false);
  const [userModel, setUserModel] = useState<{ username?: string }>({});
  const [notification, setNotification] = useState<NotificationState | null>(null);
  useInitialisedStore();
  const [cognitoUser, setCognitoUser] = useState<CognitoUser>();
  const { t } = useTranslation('common');
  const { policies, policiesAccepted } = usePolicies();
  const { user, hasSignedOut } = useAuth();
  const setTourAttributes = useSetTourAttributes();
  const browserCheckCallback = useBrowserCheckCallback();
  const source_url = useQueryState('source_url', parseAsString);
  const [amplifyConfigured, setAmplifyConfigured] = useState(false);
  const autoSSO = useAutoSSO(amplifyConfigured);
  const wrongUserTypeWarnedAlready = useRef(false);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const checkUserCalledRef = useRef(false);

  const error_desc = sessionStorage.getItem('error_description');

  const removeNotification = () => setNotification(null);

  // call once *before* returning from the first render
  useState(() => maybePersistCredentials());

  useEffect(() => {
    const checkNHSPoliciesDeclined = () => {
      const searchParams = new URLSearchParams(router.asPath.split('?')[1]);
      const urlParams = Object.fromEntries(searchParams.entries());
      const error_description = urlParams.error_description || error_desc;
      if (error_description === 'ConsentNotGiven; error=access_denied') {
        setPageView('sso');
        router.replace({ query: { optOut: 1 } }).then();
        sessionStorage.removeItem('error_description');
      }
    };
    checkNHSPoliciesDeclined();
  }, [router, router.asPath, error_desc]);

  useEffect(() => {
    if (source_url?.[0]) {
      sessionStorage.setItem('source_url', source_url[0]);
    }
  }, [source_url]);

  // Either fallback to a SSO provider derived from the error description, handle error, or remove error_description
  useEffect(() => {
    const error = error_description || error_desc;
    if (error && IDENTITY_PROVIDERS?.providers) {
      const errorCode = error.split(':')[0];
      const fallbackProviderName = `-ERR-${errorCode}`;
      const handlerProvider = IDENTITY_PROVIDERS?.providers.find((p) => p?.provider?.includes(fallbackProviderName));

      if (handlerProvider) {
        // @ts-ignore
        Auth.federatedSignIn({ provider: handlerProvider.provider }).then();
        return;
      }

      if (errorCode === MFA_ATTEMPTS_EXCEEDED_CODE) {
        setShowErrorModal(true);
      } else {
        sessionStorage.removeItem('error_description');
        const { search, origin, pathname } = window.location;
        const searchParams = new URLSearchParams(search);
        searchParams.delete('error_description');
        window.history.pushState({}, '', `${origin}${pathname}?${searchParams.toString()}`);
      }
    }
  }, [error_description, IDENTITY_PROVIDERS, error_desc]);

  useEffect(() => {
    if (notification) {
      const timeout = setTimeout(() => removeNotification(), 5 * 1000);
      return () => clearTimeout(timeout);
    }
    return () => {};
  }, [notification]);

  const stringifiedMeta = Object.keys(additionalMetaData).length ? JSON.stringify(additionalMetaData) : null;

  useEffect(() => {
    setAmplifyConfigured(true);
  }, [APP_CLIENT_ID, stringifiedMeta]);

  if (isOptOut) {
    router.replace({ query: { optOut: 1 } }).then();
  }
  const getIsUserOnProperHost = (loginUrl: string): boolean => {
    if (!loginUrl) {
      // if login_url is not set, assume the user has logged into the correct URL
      return true;
    }
    return new URL(loginUrl).hostname === window.location.hostname;
  };

  type Policies = { policiesAccepted: boolean; policies: any[] };

  const authLoginSuccess = useCallback(async (res: { user: User }, _: any, policies: Policies) => {
    if (Auth.isInHARMode) return;

    if (!('user' in res)) return;

    browserCheckCallback(true).then();

    sessionStorage.removeItem('case-filter-selected');

    const { user_type_code, /*needs_card_details,*/ self_reg_required, login_url } = res.user;

    if (!['PATIENT', 'CLINICIAN'].includes(user_type_code)) {
      await Auth.signOut().catch();
      // this gets invoked twice from two different useEffects
      if (!wrongUserTypeWarnedAlready.current) {
        wrongUserTypeWarnedAlready.current = true;
        StyledAntModal.warning({
          title: 'Incorrect User Type',
          content: `Your credentials are correct, but as a "${user_type_code}" you are not authorised to use this application. Please log in again.`,
          afterClose: () => pushToApp(login_url).then()
        });
      }
      return;
    }

    const isUserOnProperHost = getIsUserOnProperHost(login_url);
    const isLocalhostOrNgrok = getIsLocalhostOrNgrok();

    // if the user has logged into the wrong host redirect them to proper host
    if (!isUserOnProperHost && !isLocalhostOrNgrok) {
      await saveAuthAndRedirect(login_url);
      return;
    }

    // todo - when story branches work with SSO, get this set correctly
    const ssoRedirectToStoryBranch = false;

    const getTargetPage = () => {
      switch (true) {
        case !policies.policiesAccepted:
          return 'policy';
        case self_reg_required:
          return 'create-account';
        // case needs_card_details:
        //   return 'patients/profile';
        case user_type_code === 'CLINICIAN':
          return 'clinician';
        case user_type_code === 'PATIENT':
          return 'patients';
        default:
          throw Error('Could not work out where to navigate to');
      }
    };
    const targetPage = getTargetPage();
    if (ssoRedirectToStoryBranch) {
      const basePath = window.location.pathname.split('/login')[0];
      window.location.href = [basePath, targetPage].join('/');
    } else {
      await pushToApp(`/${targetPage}`);
    }
  }, []);

  useEffect(() => {
    if (!IDENTITY_PROVIDERS?.providers?.length || LOGIN_VERSION === 1) {
      setPageView('login');
    }
  }, [LOGIN_VERSION, IDENTITY_PROVIDERS]);

  useEffect(() => {
    const tempEdgeCaseUsername = sessionStorage.getItem('newUserForgotPassRedirect');

    if (tempEdgeCaseUsername) {
      sessionStorage.removeItem('newUserForgotPassRedirect');
      infoModal({
        title: 'Temporary Password Required',
        content: <>{t('We have just emailed you a copy of your temporary password. To login for the fir')}</>
      });
    }

    if (sessionStorage.getItem('passwordResetSuccess')) {
      setNotification({
        type: 'success',
        message: 'Your password has been successfully reset. You may now log in.'
      });
      sessionStorage.removeItem('passwordResetSuccess');
    }

    async function maybeNavigateToDashboard() {
      const sUrl = sessionStorage.getItem('source_url');
      const user = await tryGetUser(sUrl || undefined);

      if (user && policies) {
        await authLoginSuccess({ user }, router, { policies, policiesAccepted: Boolean(policiesAccepted) });
        setTourAttributes().then();
        if (
          (localStorage.getItem('cookie-consent') === 'all' || localStorage.getItem('cookie-consent') === 'per') &&
          GOOGLE_ANALYTICS_TAG &&
          !Auth.isInHARMode
        ) {
          if (sessionStorage.getItem('ga-configured') === 'true') {
            document.cookie = `_ga_${GOOGLE_ANALYTICS_TAG.replace(
              'G-',
              ''
            )}=; path=/; domain=.virtuallucyweb.co.uk; expires=${new Date(0).toUTCString()}`;
          }
          const options: { campaign_source?: string; campaign_id?: string } = {};
          if (sUrl) options.campaign_source = sUrl;
          if (user.client_name) options.campaign_id = user.client_name.replace(/\s/g, '-');
          ReactGA.initialize(GOOGLE_ANALYTICS_TAG, {
            gaOptions: { ...options }
          });

          sessionStorage.setItem('ga-configured', 'true');
          sessionStorage.setItem('campaign_id', user.client_name.replace(/\s/g, '-'));
        }

        sessionStorage.removeItem('source_url');
      }
    }

    maybeNavigateToDashboard().then();
  }, [authLoginSuccess, dispatch, policies, policiesAccepted, router]);

  // I do not believe this is needed anymore, but leaving it here for now. Pretty sure it's covered by the
  // preceding useEffect.
  useEffect(() => {
    if (user && policies && policies.length > 0) {
      authLoginSuccess({ user }, router, { policies, policiesAccepted: policiesAccepted || false }).then();
    }
  }, [policies, user, router, policiesAccepted, authLoginSuccess]);

  useEffect(() => {
    if (!hasSignedOut) {
      // browser check is called to notify the user if they need to update their browser
      browserCheckCallback().then();
    }
  }, []);

  useEffect(() => {
    if (hasSignedOut) {
      dispatch(requestSuccess(actionTypes.AUTH_SIGNED_OUT_SUCCESS));
    }
  }, [hasSignedOut]);

  const onUsernameSubmit = useCallback(
    async (data: { username: string; password: string }) => {
      setButtonLoading(true);
      try {
        if (COGDR && !checkUserCalledRef.current) {
          await dispatch(actions.checkUser(data.username));
          checkUserCalledRef.current = true;
          setNotification({
            type: 'info',
            message: t('A temporary password has been sent to your email')
          });
          setButtonLoading(false);
          return;
        }
        const cog = await Auth.signIn(data.username, data.password, {
          authFlowType: 'CUSTOM_WITH_SRP'
        });

        if (cog.challengeName === 'RESET_PASSWORD') {
          sessionStorage.setItem('passwordReset', data.username);
          router.push('/forgot-password').then();
          return;
        }
        const sourceURL = sessionStorage.getItem('source_url');
        const userFetchParams = sourceURL ? { source_url: sourceURL } : undefined;
        const res = await dispatch(actions.userLogin(data, userFetchParams, cog));
        setCognitoUser(cog);
        setButtonLoading(false);
        removeNotification();
        setUserModel(data);
        if (res.type === actionTypes.AUTH_LOGIN_CODE) {
          setPageView('code');
        } else if (res.type === actionTypes.AUTH_LOGIN_NEWPASSOWRD) {
          setPageView('newPassword');
        } else {
          console.log('Unhandled results type', { res });
        }
      } catch (err) {
        if (typeof err !== 'object' || !err) return;

        console.log({ err });
        setNotification({
          type: 'error',
          message: 'message' in err ? `${err.message}` : ''
        });
      } finally {
        setButtonLoading(false);
      }
    },
    [dispatch, router]
  );

  const refreshPage = () => {
    window.location.reload();
  };

  const onSubmitSendCode = useCallback(
    async (cognitoUser: CognitoUser, data: Record<string, any>) => {
      setButtonLoading(true);
      try {
        const { code } = data;
        const sourceURL = sessionStorage.getItem('source_url');
        const userFetchParams = sourceURL ? { source_url: sourceURL } : undefined;
        const { user } = await dispatch(actions.userSendCode(code.trim(), userFetchParams, cognitoUser));
        const loginURL = user.login_url;
        const isUserOnProperHost = getIsUserOnProperHost(loginURL);
        const isLocalhostOrNgrok = getIsLocalhostOrNgrok();

        // If I get to here I've supplied the correct MFA
        if (!isUserOnProperHost && !isLocalhostOrNgrok) {
          await saveAuthAndRedirect(loginURL);
          return;
        }

        replace(await deriveAppRoute(user)).then();
        sessionStorage.removeItem('source_url');
      } catch (err) {
        if (typeof err !== 'object' || !err) return;

        if ('code' in err && err.code === 'NotAuthorizedException') {
          setNotification({
            type: 'error',
            message: (
              <>
                <p css={{ marginBottom: '0.5rem' }}>{t('This code has expired.')}</p>

                <Button data-testid="login-again" type="link" onClick={refreshPage}>
                  {t('Log in again')}
                </Button>
              </>
            )
          });
        } else if ('type' in err && err.type === 'AUTH_FAIL') {
          setNotification({
            type: 'error',
            message: 'Invalid code specified'
          });
        } else if ('type' in err && err.type === 'AUTH_SESSION_EXPIRED') {
          setNotification({
            type: 'error',
            message: 'Credentials expired, please cancel and login again'
          });
        } else {
          console.log({ err });
          setNotification({
            type: 'error',
            message: 'An unknown error occurred'
          });
        }
        setButtonLoading(false);
      }
    },
    [cognitoUser, dispatch]
  );

  // Process taken upon a brand-new user logging in for the first time with a temporary password or when a user's
  // password has expired, and they need to create a new one. The flow follows:
  // - Regular log in with expired or temporary password
  // - Cognito handles to return next step as CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED
  // - Set view as <NewPasswordView>
  // - Upon entering new password this function is called
  //    - Dispatch new password to backend lambda to check its reuse and complexity
  //    - When OK'd, use Cognito confirmSignIn to change the password via Cognito
  //        - At this point, Cognito logs us in and wants to take us straight to the dashboard as confirmSignIn ignores
  //        our custom auth flow.
  //        - This is the point where the isPasswordResetting sessionStorage prop blocks the routing
  //    - Immediately log out of Cognito
  //    - Perform another log in with Cognito
  //    - This will take us to the 2FA screen
  const onResetPasswordConfirm = useCallback(
    async (data: { password: string }) => {
      setButtonLoading(true);
      // isPasswordResetting blocks the router from taking us to the dashboard/policies page upon confirmSignIn
      // fulfilling successfully and gives us time to log the user out again so we can perform another login within the
      // flow to access the 2FA screen.
      sessionStorage.setItem('isPasswordResetting', 'true');
      try {
        const { password } = data;
        const username = userModel.username || '';

        // Call backend Lambda to check password's reuse and complexity
        const res = await dispatch(actions.userNewPassword(username, password), false);
        if (res.type === actionTypes.AUTH_LOGIN_NEWPASSWORD_SUCCESS) {
          // Proposed new password is OK'd to be set by the backend but has not been changed on the backend.
          let challengeName: string;

          try {
            // Perform password change via Amplify v6 confirmSignIn challenge response.
            const challengeRes = await Auth.sendCustomChallengeAnswer({ username } as CognitoUser, password);
            challengeName = challengeRes.challengeName;
          } catch {
            console.error('Failed to save new password with confirmSignIn');
            throw new Error('Failed to save new password');
          }

          if (challengeName !== 'DONE') {
            // confirmSignIn successfully responded but did not sign in or there is a further step it wants
            console.error('Failed save new password process with confirmSignIn - further steps required');
            throw new Error('Failed to submit new password');
          }
          // At this point we have successfully changed the password within Cognito, however the confirmSignIn method
          // returns the session state that we ARE now signed in and there are no more steps to take [this method
          // for some reason IGNORES our CUSTOM_WITH_SRP auth flow].
          // Therefore, we MUST sign out at this point so we can sign in AGAIN with our CUSTOM_WITH_SRP auth flow to
          // see get the next 2FA step, otherwise onUsernameSubmit without signing out will:
          // 1 - Throw an error stating that the user is already signed in
          // 2 - Send us straight through to the dashboard without hitting the 2FA route
          // This is where we require the "isPasswordResetting" session prop to block the router from seeing a
          // logged-in user and immediately navigating to the dashboard/policy page, so we have time to log the user
          // out again.
          try {
            await signOut(); // Auth.signOut() only seems to work when you're ALREADY signed out?
          } catch {
            console.error('Failed to sign out after setting new password');
            throw new Error('Error logging in after new password set');
          }

          // Now re-sign in with custom auth and we should be directed to 2FA screen
          onUsernameSubmit({ username, password }).then();
        } else {
          const msg = await res.payload?.json();
          const message = msg?.errorMessage || 'Sorry, this password cannot be used.';

          setNotification({
            type: 'error',
            message: /^\d{3} - /.test(message) ? message.slice(6) : message
          });
        }
      } catch (err) {
        if (typeof err === 'object' && err && 'message' in err) {
          setNotification({
            type: 'error',
            message: `${err.message}`
          });
        }
      } finally {
        sessionStorage.removeItem('isPasswordResetting');
        setButtonLoading(false);
      }
    },
    [cognitoUser, dispatch, onUsernameSubmit, userdata, userModel.username]
  );

  const resendCode = useCallback(async () => {
    try {
      // Ensure signed out - We should be
      await Auth.signOut();

      const user = form.getFieldValue('username');
      const pass = form.getFieldValue('password');
      // Log in again with custom auth to re-run the 2FA path
      const cog = await Auth.signIn(user, pass, {
        authFlowType: 'CUSTOM_WITH_SRP'
      });
      setCognitoUser(cog);

      infoModal({
        title: 'Identity Confirmation Code',
        content: 'Your identity confirmation code has been resent'
      });
    } catch (e) {
      console.error(e);
      infoModal({
        title: 'Unexpected Response',
        content: 'An unexpected response was received. Please try again later.'
      });
    }
  }, [form]);

  const onMFACancel = useCallback(() => {
    setPageView('login');
  }, [setPageView]);

  if (error_description) return null;

  // if the user is about to be autosso'ed off the login page - no point in flashing up the controls
  if (autoSSO[0]) {
    return null;
  }

  // if the user record is loaded, we are already logged in - no point in flashing up the controls
  if (user) return null;

  if (SYSTEM_MODE === 'bvt' && !pathname.includes(BVT_LOGIN_PATH)) return <Maintenance />;

  const isLoginPageView = pageView === 'login';

  return (
    <LoginLayout>
      {notification && !isLoginPageView && (
        <Alert
          css={css`
            margin-bottom: 10px;
          `}
          message={notification.message}
          type={notification.type}
          showIcon
          closable
          onClose={removeNotification}
        />
      )}
      {(pageView === 'login' || !isValidPageView(pageView)) && (
        <LoginView
          onFinish={onUsernameSubmit}
          loading={buttonLoading}
          notification={notification}
          removeNotification={removeNotification}
          {...{ form }}
        />
      )}
      {pageView === 'code' && userModel.username && cognitoUser && (
        <MFAView
          onFinish={(data: Record<string, any>) => onSubmitSendCode(cognitoUser, data)}
          loading={buttonLoading}
          resendCode={resendCode}
          onCancel={onMFACancel}
        />
      )}
      {pageView === 'sso' && <SSOView setPageView={() => setPageView('login')} />}
      {pageView === 'newPassword' && userModel.username && (
        <NewPasswordView
          onFinish={onResetPasswordConfirm}
          loading={buttonLoading}
          username={userModel.username}
          {...{ form }}
        />
      )}
      <ErrorModal
        open={showErrorModal}
        onCancel={() => {
          setShowErrorModal(false);
          sessionStorage.removeItem('error_description');
          const { search, origin, pathname } = window.location;
          const searchParams = new URLSearchParams(search);

          searchParams.delete('error_description');
          window.history.pushState({}, '', `${origin}${pathname}?${searchParams.toString()}`);
        }}
        title={t(
          'You’ve reached the maximum number of attempts to enter your verification code. Please wait and try again shortly.'
        )}
      />
    </LoginLayout>
  );
};

function WrappedLogin() {
  if (!window) {
    return null;
  }

  return <Login />;
}

export default WrappedLogin;
export const Route = createFileRoute('/login')({ component: WrappedLogin });
